/**
 * Krok 4: Szczenięta / Mioty (in-wizard quick add)
 */
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Baby, Plus, Trash2, Edit2, Check, Calendar } from 'lucide-react';

const STATUS_OPTIONS = [
  { id: 'planned', label: 'Planowany miot' },
  { id: 'available', label: 'Dostępne szczenięta' },
  { id: 'reserved', label: 'Zarezerwowane' },
  { id: 'sold_out', label: 'Wszystkie sprzedane' },
];

function LitterForm({ litter, breeds, onSave, onCancel }) {
  const [form, setForm] = useState(
    litter || { breed: breeds?.[0] || '', status: 'planned', date: '', description: '' }
  );
  return (
    <div className="rounded-2xl border-2 border-orange-200 bg-white shadow-lg p-5 space-y-4" data-testid="wizard-v2-litter-form">
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">1. Rasa</Label>
        <Select value={form.breed} onValueChange={(v) => setForm({ ...form, breed: v })}>
          <SelectTrigger className="h-11" data-testid="wizard-v2-litter-breed"><SelectValue placeholder="Wybierz rasę…" /></SelectTrigger>
          <SelectContent>
            {(breeds || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            {(!breeds || breeds.length === 0) && <SelectItem value="other">Inna rasa</SelectItem>}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">2. Status miotu</Label>
        <div className="grid grid-cols-2 gap-2">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setForm({ ...form, status: s.id })}
              data-testid={`wizard-v2-litter-status-${s.id}`}
              className={`px-3 py-2.5 rounded-lg text-sm font-medium border text-left ${
                form.status === s.id ? 'border-orange-400 bg-orange-50 text-orange-900' : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-orange-500" />
          3. Data {form.status === 'planned' ? 'planowanego krycia/porodu' : 'urodzenia'}
        </Label>
        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11" data-testid="wizard-v2-litter-date" />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">4. Krótki opis (rodzice, charakter, plany)</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="np. Mama: Złota Marzenie FCI Bella (HD-A). Ojciec: Champion Polski Ares (HD-A, ED-0/0)…"
          rows={3}
          maxLength={300}
          data-testid="wizard-v2-litter-description"
          className="resize-none"
        />
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="wizard-v2-litter-cancel">Anuluj</Button>
        <Button type="button" onClick={() => onSave(form)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="wizard-v2-litter-save">
          <Check className="w-4 h-4 mr-1.5" /> Zapisz miot
        </Button>
      </div>
    </div>
  );
}

export default function Step4Litters({ data, updateData }) {
  const litters = data.litters || [];
  const [editingIdx, setEditingIdx] = useState(null);
  const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.id, s.label]));

  const save = (litter) => {
    if (editingIdx === -1) updateData({ litters: [...litters, litter] });
    else updateData({ litters: litters.map((l, i) => (i === editingIdx ? litter : l)) });
    setEditingIdx(null);
  };

  return (
    <div className="space-y-6" data-testid="wizard-v2-step-litters">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <Baby className="w-5 h-5 text-orange-500" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Szczenięta / Mioty</h2>
        </div>
        <p className="text-gray-500 text-sm">
          Dodaj aktualne lub planowane mioty. Klienci szukają konkretu — daty, rasy, statusu.
        </p>
      </header>

      {litters.length > 0 && editingIdx === null && (
        <div className="space-y-2">
          {litters.map((l, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white" data-testid={`wizard-v2-litter-card-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                <Baby className="w-6 h-6 text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{l.breed || 'Miot'}</p>
                <p className="text-xs text-gray-500">{STATUS_LABEL[l.status]} {l.date && ` · ${l.date}`}</p>
              </div>
              <button type="button" onClick={() => setEditingIdx(i)} className="p-2 text-gray-400 hover:text-orange-500"><Edit2 className="w-4 h-4" /></button>
              <button type="button" onClick={() => updateData({ litters: litters.filter((_, idx) => idx !== i) })} className="p-2 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => setEditingIdx(-1)} data-testid="wizard-v2-add-litter-btn" className="w-full border-dashed border-orange-300 text-orange-600 hover:bg-orange-50">
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj kolejny miot
          </Button>
        </div>
      )}

      {editingIdx !== null && (
        <LitterForm
          litter={editingIdx >= 0 ? litters[editingIdx] : null}
          breeds={data.breeds || []}
          onSave={save}
          onCancel={() => setEditingIdx(null)}
        />
      )}

      {litters.length === 0 && editingIdx === null && (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center">
          <Baby className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-3">Brak miotów. Możesz pominąć i dodać później.</p>
          <Button type="button" onClick={() => setEditingIdx(-1)} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj miot
          </Button>
        </div>
      )}
    </div>
  );
}
